import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { ref } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useChangePasswordUser } from "../../api/use-user";
import { ToastContext } from "../../contexts/toast-context";

export const ChangePassword = ({ open, onClose, userId }) => {
  const { t } = useTranslation(["user", "validation", "common"]);
  const { showSuccess } = useContext(ToastContext);

  const schema = yup.object({
    oldPassword: yup
      .string()
      .required(t("requiredValidation", { field: t("oldPassword") })),
    password: yup
      .string()
      .min(8, t("minLengthValidation", { field: t("newPassword"), min: 7 }))
      .required(t("requiredValidation", { field: t("newPassword") })),
    passwordAgain: yup
      .string()
      .oneOf([ref("password")], t("passwordDontMatch"))
      .required(t("requiredValidation", { field: t("newPasswordAgain") })),
  });

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
    reset,
  } = useForm({
    defaultValues: {
      oldPassword: "",
      password: "",
      passwordAgain: "",
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const [{ loading, error }, execute] = useChangePasswordUser(userId);
  const errorMessage =
    error && error.response?.status !== 401 &&
    (error.response?.data?.error_message ||
      t("genericError", { action: t("changePassword") }));

  useEffect(() => {
    if (error?.response?.status === 401)
      setError("oldPassword", { message: t("oldPasswordWrong") });
  }, [error, setError, t]);

  const onSubmit = async (data) => {
    const actualData = {...data};
    delete actualData.passwordAgain;
    await execute({ data: actualData });
    onClose();
    showSuccess(t("successfulAction"));
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t("changePassword")}</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            "& > .MuiFormControl-root": {
              mb: 3,
            },
          }}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="oldPassword"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.oldPassword}
                helperText={errors.oldPassword?.message}
                label={t("oldPassword")}
                autoComplete="off"
                type="password"
                {...field}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                label={t("newPassword")}
                autoComplete="new-password"
                type="password"
                {...field}
              />
            )}
          />
          <Controller
            name="passwordAgain"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.passwordAgain}
                helperText={errors.passwordAgain?.message}
                label={t("newPasswordAgain")}
                autoComplete="new-password"
                type="password"
                {...field}
              />
            )}
          />
          {errorMessage && (
            <FormHelperText error>{errorMessage}</FormHelperText>
          )}
        </Box>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={30} sx={{ mt: 3 }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={loading}>
          {t("cancel")}
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={loading} autoFocus>
          {t("changePassword")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
